/**
 * 跨子域广告归因（Cookie，30 天）。
 *
 * 保存精简的广告点击信息到 .xfyun.cn Cookie，供 console 等子域读取。
 */

const COOKIE_KEY = 'xfyun_ad_attribution'
const COOKIE_DOMAIN = '.xfyun.cn'
const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60
const MAX_CLICK_ID_ENCODED_LENGTH = 1024
const MAX_COOKIE_VALUE_LENGTH = 3800
const SENSITIVE_QUERY_PARAM_NAMES = ['token', 'account_id', 'accountid', 'ssosessionid']

// 配置顺序同时决定 URL 中存在多个广告参数时的匹配优先级。
const CLICK_ID_CONFIG = [
  { name: 'bd_vid', platform: 'BAIDU' },
  { name: 'qhclickid', platform: 'QIHOO360' },
  { name: 'msclkid', platform: 'BING' }
]

function isValidClickId (value) {
  if (typeof value !== 'string' || value.trim() === '' || /[\r\n]/.test(value)) return false

  try {
    return encodeURIComponent(value).length <= MAX_CLICK_ID_ENCODED_LENGTH
  } catch (e) {
    return false
  }
}

function getQueryParam (search, name) {
  const query = (search || '').replace(/^\?/, '').split('&')
  for (let i = 0; i < query.length; i += 1) {
    const pair = query[i].split('=')
    let decodedName
    try {
      decodedName = decodeURIComponent(pair[0] || '')
    } catch (e) {
      decodedName = null
    }

    if (decodedName === name) {
      try {
        return decodeURIComponent((pair.slice(1).join('=') || '').replace(/\+/g, ' '))
      } catch (e) {
        return null
      }
    }
  }
  return null
}

function isXfyunDomain (hostname) {
  return hostname === 'xfyun.cn' || /\.xfyun\.cn$/i.test(hostname)
}

function sanitizeLandingUrl (url) {
  const hashIndex = url.indexOf('#')
  const urlWithoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex)
  const queryIndex = urlWithoutHash.indexOf('?')
  if (queryIndex === -1) return urlWithoutHash

  const baseUrl = urlWithoutHash.slice(0, queryIndex)
  const safeQuery = urlWithoutHash
    .slice(queryIndex + 1)
    .split('&')
    .filter((pair) => {
      const rawName = pair.split('=')[0] || ''
      try {
        return SENSITIVE_QUERY_PARAM_NAMES.indexOf(decodeURIComponent(rawName).toLowerCase()) === -1
      } catch (e) {
        return true
      }
    })
    .join('&')

  return safeQuery ? `${baseUrl}?${safeQuery}` : baseUrl
}

function validateAttribution (value) {
  const config = value && CLICK_ID_CONFIG.find((item) => {
    return item.platform === value.platform && item.name === value.clickIdName
  })
  if (
    !config ||
    !isValidClickId(value.clickIdValue) ||
    typeof value.landingUrl !== 'string' ||
    value.landingUrl === ''
  ) {
    return null
  }
  return value
}

function readCookie () {
  if (typeof document === 'undefined') return null

  try {
    const cookies = document.cookie ? document.cookie.split(';') : []
    for (let i = 0; i < cookies.length; i += 1) {
      const item = cookies[i].trim()
      const separatorIndex = item.indexOf('=')
      const rawName = separatorIndex === -1 ? item : item.slice(0, separatorIndex)
      if (rawName === encodeURIComponent(COOKIE_KEY)) {
        const rawValue = separatorIndex === -1 ? '' : item.slice(separatorIndex + 1)
        try {
          const value = validateAttribution(JSON.parse(decodeURIComponent(rawValue)))
          if (value) return value
        } catch (e) {
          // 同名旧 Cookie 可能无效，继续查找其他作用域下的有效值。
        }
      }
    }
  } catch (e) {
    return null
  }
  return null
}

function writeCookie (attribution) {
  let storedAttribution = attribution
  let encodedValue

  try {
    encodedValue = encodeURIComponent(JSON.stringify(storedAttribution))
    if (encodedValue.length > MAX_COOKIE_VALUE_LENGTH) {
      storedAttribution = Object.assign({}, storedAttribution, {
        landingUrl: storedAttribution.landingUrl.split('?')[0]
      })
      encodedValue = encodeURIComponent(JSON.stringify(storedAttribution))
    }
  } catch (e) {
    return null
  }

  if (encodedValue.length > MAX_COOKIE_VALUE_LENGTH) return null

  const expires = new Date(Date.now() + COOKIE_MAX_AGE_SECONDS * 1000)
  const cookie = [
    `${encodeURIComponent(COOKIE_KEY)}=${encodedValue}`,
    'Path=/',
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
    `Expires=${expires.toUTCString()}`,
    'SameSite=Lax'
  ]

  if (isXfyunDomain(window.location.hostname)) cookie.push(`Domain=${COOKIE_DOMAIN}`)
  if (window.location.protocol === 'https:') cookie.push('Secure')

  try {
    document.cookie = cookie.join('; ')
  } catch (e) {
    return null
  }

  return readCookie()
}

/** 捕获首个有效广告点击 ID，并通过父域 Cookie 共享给 xfyun.cn 的所有子域。 */
export function captureAdAttribution () {
  if (typeof window === 'undefined' || !window.location) return null

  const existing = readCookie()
  if (existing) return existing

  const config = CLICK_ID_CONFIG.find((item) => {
    return isValidClickId(getQueryParam(window.location.search, item.name))
  })
  if (!config) return null

  const attribution = {
    platform: config.platform,
    clickIdName: config.name,
    clickIdValue: getQueryParam(window.location.search, config.name),
    landingUrl: sanitizeLandingUrl(window.location.href)
  }
  return writeCookie(attribution)
}

export { COOKIE_KEY, CLICK_ID_CONFIG }

// 尽早执行，避免子组件跳转或替换地址后丢失落地页中的广告参数。
captureAdAttribution()



// WEBPACK FOOTER //
// ./src/js/libs/ad-attribution.js